import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { EnvironmentService } from "./environment.service";

export interface RequestOptions {
  observe?: "body";
  params?: HttpParams | { [param: string]: string | string[] };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class HttpClientService {
  private authHeaders: { headers: HttpHeaders } | undefined;
  public token: string;
  headers: HttpHeaders;

  constructor(private http: HttpClient, private environmentService: EnvironmentService) {}

  private get endpoint(): string {
    return this.environmentService.get("api.url") || "";
  }

  public get<T = any>(url: string, options: RequestOptions = {}): Observable<T> {
    return this.ensureAuthorization().pipe(
      mergeMap(() => {
        const all_ops = Object.assign({}, this.authHeaders, options);
        return this.http.get<T>(this.endpoint + url, all_ops);
      })
    );
  }

  public post(url: string, body: any, options: RequestOptions = {}): Observable<any> {
    return this.ensureAuthorization().pipe(
      mergeMap(() => {
        const all_ops = Object.assign({}, this.authHeaders, options);
        return this.http.post(this.endpoint + url, body, all_ops);
      })
    );
  }

  public put(url: string, body: any, options: RequestOptions = {}): Observable<any> {
    return this.ensureAuthorization().pipe(
      mergeMap(() => {
        const all_ops = Object.assign({}, this.authHeaders, options);
        return this.http.put(this.endpoint + url, body, all_ops);
      })
    );
  }

  public patch(url: string, body: any, options: RequestOptions = {}): Observable<any> {
    return this.ensureAuthorization().pipe(
      mergeMap(() => {
        const all_ops = Object.assign({}, this.authHeaders, options);
        return this.http.patch(this.endpoint + url, body, all_ops);
      })
    );
  }

  public delete(url: string, options: RequestOptions = {}): Observable<any> {
    return this.ensureAuthorization().pipe(
      mergeMap(() => {
        const all_ops = Object.assign({}, this.authHeaders, options);
        return this.http.delete(this.endpoint + url, all_ops);
      })
    );
  }

  public getWithoutAuth(url: string, options: RequestOptions = {}): Observable<any> {
    const all_ops = Object.assign({}, options);
    return this.http.get(this.endpoint + url, all_ops);
  }

  public postWithoutAuth(url: string, body: any, options: RequestOptions = {}): Observable<any> {
    const all_ops = Object.assign({}, options);
    return this.http.post(this.endpoint + url, body, all_ops);
  }

  public putWithoutAuth(url: string, body: unknown, options: RequestOptions = {}): Observable<unknown> {
    const all_ops = Object.assign({}, options);
    return this.http.put(this.endpoint + url, body, all_ops);
  }

  private ensureAuthorization(): Observable<void> {
    return new Observable((observer) => {
      const access_token = localStorage.getItem("access_token");
      if (access_token) {
        this.setAuthHeaders(access_token);
      } else {
        // Clear cached headers so logout does not keep sending a stale Bearer.
        this.authHeaders = undefined;
      }
      observer.next();
      observer.complete();
    });
  }

  private setAuthHeaders(access_token: string): void {
    this.authHeaders = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${access_token}`,
      }),
    };
  }
}
